import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import useRailgunContext from '@web/hooks/useRailgunContext'
import { getMissingPOIUsdByChain, getTotalBalanceUSD } from '@web/utils/railgunTokens'
import { ModeEnum } from '@web/contexts/appModeContext'

const BALANCE_LOAD_HINT_THRESHOLD_MS = 15 * 1000 // 15 seconds

export type RailgunOverviewBalanceResult = {
  totalRailgunBalanceForDisplay: number
  totalRailgunBalanceFormatted: { integer: string; decimal: string }
  isRailgunBalanceFetching: boolean
  fetchRailgunBalance: () => Promise<void>
  showFirstBalanceLoadHint: boolean
  hasPriorBalance: boolean
  totalPendingUSD: number
  totalMissingInternalPOIUSD: number
  totalMissingExternalPOIUSD: number
  hasPendingBalance: boolean
  cachePoisoned: boolean
  cachePoisonedNetworks: string[]
}

/**
 * Encapsulates Railgun balance and pending/POI state for the dashboard overview in private mode.
 */
export function useRailgunOverviewBalance(
  mode: 'public' | 'private'
): RailgunOverviewBalanceResult {
  const {
    currentRailgunWallet,
    isBalanceFetching: isRailgunBalanceFetching,
    fetchBalance: fetchRailgunBalance
  } = useRailgunContext()

  const hasPriorBalance = Boolean(currentRailgunWallet?.balanceLastUpdatedAt)

  const [showFirstBalanceLoadHint, setShowFirstBalanceLoadHint] = useState(false)
  const balanceLoadHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isRailgunBalanceFetchingRef = useRef(isRailgunBalanceFetching)
  isRailgunBalanceFetchingRef.current = isRailgunBalanceFetching
  const hasPriorBalanceRef = useRef(hasPriorBalance)
  hasPriorBalanceRef.current = hasPriorBalance

  useEffect(() => {
    // Suppress the "Initial load may take 15 minutes" banner once any prior sync has
    // completed for this account — a refresh isn't a first-time load. Phase 0.4 of
    // RAILGUN_HYBRID_REWRITE_PLAN.md.
    if (mode !== ModeEnum.PRIVATE || !isRailgunBalanceFetching || hasPriorBalance) {
      if (balanceLoadHintTimeoutRef.current) {
        clearTimeout(balanceLoadHintTimeoutRef.current)
        balanceLoadHintTimeoutRef.current = null
      }
      setShowFirstBalanceLoadHint(false)
      return
    }
    balanceLoadHintTimeoutRef.current = setTimeout(() => {
      if (isRailgunBalanceFetchingRef.current && !hasPriorBalanceRef.current) {
        setShowFirstBalanceLoadHint(true)
      }
      balanceLoadHintTimeoutRef.current = null
    }, BALANCE_LOAD_HINT_THRESHOLD_MS)
    return () => {
      if (balanceLoadHintTimeoutRef.current) {
        clearTimeout(balanceLoadHintTimeoutRef.current)
        balanceLoadHintTimeoutRef.current = null
      }
    }
  }, [mode, isRailgunBalanceFetching])

  const totalRailgunBalanceForDisplay = useMemo(() => {
    if (mode !== ModeEnum.PRIVATE) return 0
    return (
      currentRailgunWallet?.totalBalanceUSD ??
      (currentRailgunWallet?.balance != null ? Number(currentRailgunWallet.balance) : 0)
    )
  }, [mode, currentRailgunWallet?.totalBalanceUSD, currentRailgunWallet?.balance])

  const totalRailgunBalanceFormatted = useMemo(() => {
    const [integer, decimal] = formatDecimals(totalRailgunBalanceForDisplay, 'value').split('.')
    return { integer: integer ?? '0', decimal: decimal ?? '' }
  }, [totalRailgunBalanceForDisplay])

  const totalPendingUSD = useMemo(() => {
    if (mode !== ModeEnum.PRIVATE) return 0
    const pending = currentRailgunWallet?.pendingBalanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    if (!pending?.size || !prices?.size) return 0
    return getTotalBalanceUSD(pending, prices)
  }, [
    mode,
    currentRailgunWallet?.pendingBalanceTokenMap,
    currentRailgunWallet?.balanceTokenPriceMap
  ])

  // POI bucket notes can carry corrupt/garbage amounts (e.g. right after a Shield,
  // before the engine finalizes the note). getTotalBalanceUSD divides by decimals but
  // does NOT drop these, so a single corrupt note overflows the "Incoming $X" pill.
  // getMissingPOIUsdByChain applies the MAX_SANE_POI_NOTE_AMOUNT guard — use it here.
  const totalMissingInternalPOIUSD = useMemo(() => {
    if (mode !== ModeEnum.PRIVATE) return 0
    const map = currentRailgunWallet?.missingInternalPOIBalanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    if (!map?.size || !prices?.size) return 0
    let total = 0
    for (const v of getMissingPOIUsdByChain(map, undefined, prices).values()) total += v
    return total
  }, [
    mode,
    currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
    currentRailgunWallet?.balanceTokenPriceMap
  ])

  const totalMissingExternalPOIUSD = useMemo(() => {
    if (mode !== ModeEnum.PRIVATE) return 0
    const map = currentRailgunWallet?.missingExternalPOIBalanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    if (!map?.size || !prices?.size) return 0
    let total = 0
    for (const v of getMissingPOIUsdByChain(undefined, map, prices).values()) total += v
    return total
  }, [
    mode,
    currentRailgunWallet?.missingExternalPOIBalanceTokenMap,
    currentRailgunWallet?.balanceTokenPriceMap
  ])

  const hasPendingBalance = useMemo(() => {
    if (mode !== ModeEnum.PRIVATE) return false
    const maps = [
      currentRailgunWallet?.pendingBalanceTokenMap,
      currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
      currentRailgunWallet?.missingExternalPOIBalanceTokenMap
    ]
    for (const map of maps) {
      if (!map?.size) continue
      for (const balances of map.values()) {
        if (balances.some((b) => b.amount > 0n)) return true
      }
    }
    return false
  }, [
    mode,
    currentRailgunWallet?.pendingBalanceTokenMap,
    currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
    currentRailgunWallet?.missingExternalPOIBalanceTokenMap
  ])

  const fetchRailgunBalanceStable = useCallback(async () => {
    await fetchRailgunBalance()
  }, [fetchRailgunBalance])

  const cachePoisoned = mode === ModeEnum.PRIVATE && Boolean(currentRailgunWallet?.cachePoisoned)
  const cachePoisonedNetworks = useMemo(
    () => (cachePoisoned ? currentRailgunWallet?.cachePoisonedNetworks ?? [] : []),
    [cachePoisoned, currentRailgunWallet?.cachePoisonedNetworks]
  )

  return {
    totalRailgunBalanceForDisplay,
    totalRailgunBalanceFormatted,
    isRailgunBalanceFetching,
    fetchRailgunBalance: fetchRailgunBalanceStable,
    showFirstBalanceLoadHint,
    hasPriorBalance,
    totalPendingUSD,
    totalMissingInternalPOIUSD,
    totalMissingExternalPOIUSD,
    hasPendingBalance,
    cachePoisoned,
    cachePoisonedNetworks
  }
}
